import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast"; // For success/error messages
import { FiLock, FiKey, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle } from "react-icons/fi"; // Icons

const ChangePasswordForm = () => { // Renamed component to match usage in MyAccount.js
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/"; // Fallback
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  // Removed successMessage state, using toast for success notifications
  const [loading, setLoading] = useState(false);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const gradientColor = "linear-gradient(to right, #e5272e, #f59332)";
  const accentColor = "#e5272e"; // Red from gradient for accents

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear specific error when user starts typing in that field
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.oldPassword) {
      newErrors.oldPassword = "Old password is required.";
    }
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required.";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters.";
    }
    if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your new password.";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "New passwords do not match.";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!token) {
      toast.error("Authentication token not found. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${baseUrl}auth/change-password`, // Ensure this endpoint is correct
        {
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.success) { // Check for success flag from backend
        toast.success(response.data.message || "Password changed successfully!");
        setFormData({ oldPassword: "", newPassword: "", confirmPassword: "" }); // Reset form
        setErrors({}); // Clear any previous errors
      } else {
        // Handle specific error messages from backend if available
        toast.error(response.data.message || "Failed to change password. Please try again.");
        // Optionally, set specific field errors if backend indicates them
        // For example: if (response.data.field === 'oldPassword') setErrors({ oldPassword: response.data.message });
      }
    } catch (err) {
      console.error("Password change error:", err);
      const errorMessage = err.response?.data?.message || "An unexpected error occurred. Please try again.";
      toast.error(errorMessage);
      // If the error is about the old password, you might want to set it specifically
      if (errorMessage.toLowerCase().includes("old password") || err.response?.status === 401 || err.response?.status === 400) {
        setErrors(prev => ({...prev, oldPassword: "Old password might be incorrect."}));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <style>
    {`
        .change-password-card {
            max-width: 600px;
            border-radius: 16px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
            border: none;
        }
        .change-password-header {
            background-image: ${gradientColor};
            color: white;
            border-top-left-radius: 16px;
            border-top-right-radius: 16px;
            padding: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .change-password-header h4 {
            font-weight: 600;
            margin: 0;
            margin-left: 10px;
        }
        .form-label-cp { /* cp for Change Password */
            font-weight: 500;
            color: #495057;
            font-size: 0.9rem;
            margin-bottom: 0.5rem;
        }
        .form-control-cp {
            border-radius: 8px;
            padding: 0.75rem 1rem;
            padding-left: 2.8rem; /* Space for icon */
            border: 1px solid #ced4da;
            font-size: 1rem;
        }
        .form-control-cp:focus {
            border-color: ${accentColor};
            box-shadow: 0 0 0 0.2rem rgba(229, 39, 46, 0.2);
        }
        .input-group-cp {
            position: relative;
        }
        .input-icon-cp {
            position: absolute;
            left: 15px;
            top: 50%;
            transform: translateY(-50%);
            color: #adb5bd;
            font-size: 1.1rem;
        }
        .password-toggle-icon-cp {
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);
            color: #777;
            cursor: pointer;
            font-size: 1.2rem;
        }
        .btn-submit-cp {
            background-image: ${gradientColor};
            color: white;
            border: none;
            padding: 0.8rem 1.5rem;
            border-radius: 50px;
            font-weight: 600;
            font-size: 1rem;
            transition: all 0.3s ease;
            width: 100%;
        }
        .btn-submit-cp:hover {
            opacity: 0.85;
            color: white;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(229, 39, 46, 0.2);
        }
        .btn-submit-cp:disabled {
            opacity: 0.7;
        }
        .forgot-password-link-cp {
            color: ${accentColor};
            font-weight: 500;
            font-size: 0.85rem;
            text-decoration: none;
        }
        .forgot-password-link-cp:hover {
            text-decoration: underline;
        }
        .error-text-cp {
            font-size: 0.8rem;
            color: #dc3545;
        }
        .success-message-cp { /* Not used if using toast, but kept for reference */
            background-color: #d1e7dd;
            border-color: #badbcc;
            color: #0f5132;
        }
    `}
    </style>
    <div className="container py-4 d-flex justify-content-center align-items-center">
      <div className="card shadow-lg border-0 change-password-card w-100 p-2 p-md-3"> {/* Added padding to card */}
        <div className="card-header change-password-header mb-4">
          <FiKey size={28} />
          <h4 className="m-0">Change Your Password</h4>
        </div>
        <div className="card-body pt-2"> {/* Adjusted padding top */}
          <form onSubmit={handleSubmit} noValidate>
            {/* Success messages will be handled by react-hot-toast */}
            
            <div className="mb-3">
              <label className="form-label form-label-cp" htmlFor="oldPassword">Old Password</label>
              <div className="input-group-cp">
               
                <input
                  type={showOldPassword ? "text" : "password"}
                  className={`form-control form-control-cp ${errors.oldPassword ? "is-invalid" : ""}`}
                  id="oldPassword"
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  placeholder="Enter your current password"
                />
                <span onClick={() => setShowOldPassword(!showOldPassword)} className="password-toggle-icon-cp">
                    {showOldPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
              {errors.oldPassword && <div className="invalid-feedback d-block error-text-cp">{errors.oldPassword}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label form-label-cp" htmlFor="newPassword">New Password</label>
              <div className="input-group-cp">
               
                <input
                  type={showNewPassword ? "text" : "password"}
                  className={`form-control form-control-cp ${errors.newPassword ? "is-invalid" : ""}`}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password (min. 6 characters)"
                />
                 <span onClick={() => setShowNewPassword(!showNewPassword)} className="password-toggle-icon-cp">
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
              {errors.newPassword && <div className="invalid-feedback d-block error-text-cp">{errors.newPassword}</div>}
            </div>

            <div className="mb-4"> {/* Increased margin bottom */}
              <label className="form-label form-label-cp" htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-group-cp">
                
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-control form-control-cp ${errors.confirmPassword ? "is-invalid" : ""}`}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your new password"
                />
                 <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="password-toggle-icon-cp">
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
              {errors.confirmPassword && (
                <div className="invalid-feedback d-block error-text-cp">{errors.confirmPassword}</div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-submit-cp"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Changing...
                </>
              ) : (
                "Update Password"
              )}
            </button>

            {/* "Forgot Password?" link can be added if you have that functionality */}
            {/* <div className="text-center mt-3">
              <Link to="/forgot-password" className="forgot-password-link-cp">
                Forgot Password?
              </Link>
            </div> */}
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default ChangePasswordForm; // Renamed export
